import { ApiData, ApiDescription, RequestsMethod } from '../clientTypes';

export type CoursePlanStatus = 'queued' | 'running' | 'completed' | 'failed';

export type CoursePlanSourceType = 'agora' | 'lms' | 'transcript' | 'feedback' | 'web' | 'ai';

export type CoursePlanSourceDTO = {
  type: CoursePlanSourceType;
  ref: string;
  note: string;
};

export type CoursePlanPointDTO = {
  text: string;
  sources: CoursePlanSourceDTO[];
};

export type CoursePlanLessonDTO = {
  title: string;
  description: string;
  activities: string[];
  homework: string;
  materials: string[];
  sources: CoursePlanSourceDTO[];
};

export type CoursePlanModuleDTO = {
  title: string;
  goal: string;
  sessions: number;
  lessons: CoursePlanLessonDTO[];
  sources: CoursePlanSourceDTO[];
};

export type CoursePlanContentDTO = {
  title: string;
  summary: string;
  targetAudience: string;
  durationWeeks: number;
  sessionsPlanned: number;
  prerequisites: CoursePlanPointDTO[];
  learningOutcomes: CoursePlanPointDTO[];
  modules: CoursePlanModuleDTO[];
  assessment: CoursePlanPointDTO[];
  mentorGuidelines: CoursePlanPointDTO[];
  gapsInCurrentContent: CoursePlanPointDTO[];
  changesVsCurrent: CoursePlanPointDTO[];
};

export type CoursePlanOptionsDTO = {
  windowMonths: number;
  focus?: string;
  language: 'en' | 'ro';
};

export type CoursePlanSourcesDTO = {
  agora: {
    hasDescription: boolean;
    legacyModules: number;
    legacyLessons: number;
    knowledgeBaseArticles: number;
  };
  lms?: { title?: string; sections: number; units: number; error?: string };
  feedback: { sessionsWithFeedback: number; used: number };
  transcripts: {
    meetingsWithTranscript: number;
    used: number;
    characters: number;
    batches: number;
  };
  web: { queries: string[]; citations: { title: string; url: string }[]; error?: string };
};

export type CoursePlanDigestDTO = {
  batch: number;
  meetings: number;
  from?: string;
  to?: string;
  digest: Record<string, unknown>;
};

export type CoursePlanReviewStatus = 'in_review' | 'update_requested' | 'revising' | 'approved';

export type CoursePlanCommentRef = {
  section: string;
  moduleIndex?: number;
  lessonIndex?: number;
  label: string;
};

export type CoursePlanActorDTO = { _id: string; fullName?: string };

export type CoursePlanCommentDTO = {
  _id: string;
  author: CoursePlanActorDTO;
  text: string;
  ref: CoursePlanCommentRef;
  createdAt: string;
  addressedInVersion?: number;
};

export type CoursePlanRevisionDTO = {
  version: number;
  plan: CoursePlanContentDTO;
  requestNote?: string;
  requestedBy?: CoursePlanActorDTO;
  revisedAt: string;
  revisionNotes?: string[];
  commentIds: string[];
};

export type CoursePlanReviewDTO = {
  status: CoursePlanReviewStatus;
  requestNote?: string;
  requestedBy?: CoursePlanActorDTO;
  requestedAt?: string;
  declineNote?: string;
  approvedBy?: CoursePlanActorDTO;
  approvedAt?: string;
  revisionError?: string;
};

export type CoursePlanSummaryDTO = {
  _id: string;
  course: { _id: string; title: string };
  status: CoursePlanStatus;
  stage?: string;
  progress?: { done: number; total: number };
  options: CoursePlanOptionsDTO;
  model?: string;
  error?: string;
  createdBy?: { _id: string; fullName?: string };
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  planTitle?: string;
  version: number;
  review?: CoursePlanReviewDTO;
  commentCount: number;
  openCommentCount: number;
};

export type CoursePlanDTO = CoursePlanSummaryDTO & {
  sources?: CoursePlanSourcesDTO;
  research?: { markdown: string };
  digests?: CoursePlanDigestDTO[];
  plan?: CoursePlanContentDTO;
  comments?: CoursePlanCommentDTO[];
  revisions?: CoursePlanRevisionDTO[];
  revisionNotes?: string[];
};

export const createCoursePlan: ApiDescription = {
  endpoint: '/admin/course-plans',
  method: RequestsMethod.Post,
};

export type CreateCoursePlan = ApiData<
  { courseId: string; options?: Partial<CoursePlanOptionsDTO> },
  CoursePlanDTO
>;

export const listCoursePlans: ApiDescription = {
  endpoint: '/admin/course-plans',
  method: RequestsMethod.Get,
};

export type ListCoursePlans = ApiData<{ courseId?: string }, CoursePlanSummaryDTO[]>;

export const getCoursePlan: ApiDescription = {
  endpoint: '/admin/course-plans/:id',
  method: RequestsMethod.Get,
};

export type GetCoursePlan = ApiData<unknown, CoursePlanDTO>;

export const deleteCoursePlan: ApiDescription = {
  endpoint: '/admin/course-plans/:id',
  method: RequestsMethod.Delete,
};

export type DeleteCoursePlan = ApiData<unknown, { message: string }>;

export const listCoursePlanReviews: ApiDescription = {
  endpoint: '/admin/course-plans/reviews',
  method: RequestsMethod.Get,
};

export type ListCoursePlanReviews = ApiData<unknown, CoursePlanSummaryDTO[]>;

export const addCoursePlanComment: ApiDescription = {
  endpoint: '/admin/course-plans/:id/comments',
  method: RequestsMethod.Post,
};

export type AddCoursePlanComment = ApiData<
  { text: string; ref: CoursePlanCommentRef },
  CoursePlanDTO
>;

export const deleteCoursePlanComment: ApiDescription = {
  endpoint: '/admin/course-plans/:id/comments/delete',
  method: RequestsMethod.Post,
};

export type DeleteCoursePlanComment = ApiData<{ commentId: string }, CoursePlanDTO>;

export const requestCoursePlanUpdate: ApiDescription = {
  endpoint: '/admin/course-plans/:id/request-update',
  method: RequestsMethod.Post,
};

export type RequestCoursePlanUpdate = ApiData<{ note?: string }, CoursePlanDTO>;

export const resolveCoursePlanUpdateRequest: ApiDescription = {
  endpoint: '/admin/course-plans/:id/update-request',
  method: RequestsMethod.Post,
};

export type ResolveCoursePlanUpdateRequest = ApiData<
  { decision: 'approve' | 'decline'; note?: string },
  CoursePlanDTO
>;

export const approveCoursePlan: ApiDescription = {
  endpoint: '/admin/course-plans/:id/approve',
  method: RequestsMethod.Post,
};

export type ApproveCoursePlan = ApiData<unknown, CoursePlanDTO>;

export const reopenCoursePlan: ApiDescription = {
  endpoint: '/admin/course-plans/:id/reopen',
  method: RequestsMethod.Post,
};

export type ReopenCoursePlan = ApiData<unknown, CoursePlanDTO>;
